import React, { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Backdrop,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CategoryIcon from "@mui/icons-material/Category";
import EditIcon from "@mui/icons-material/Edit";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import DescriptionIcon from "@mui/icons-material/Description";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { useLocation, useNavigate } from "react-router-dom";
import { OverAllLoader } from "../../components/OverAllLoader";
import { LoadingOverlay } from "../../components/LoadingOverlay";

const validationSchema = Yup.object().shape({
  program_id: Yup.object().required("Job Role is required"),
  topic: Yup.string().nullable(),
  experience_level: Yup.number()
    .typeError("Experience Years must be a number")
    .required("Experience Years is required")
    .min(0, "Experience years must be at least 0")
    .max(99, "Experience years cannot exceed 2 digits."),
  question_type: Yup.string().required("Question Type is required"),
  difficulty: Yup.string().required("Difficulty is required"),
  no_of_questions: Yup.number()
    .typeError("Number of Questions must be a number")
    .required("Number of Questions is required")
    .min(1, "At least 1 question is required")
    .max(25, "Number of questions cannot exceed 25."),
});

const questionTypeOptions = ["TECHNICAL", "SCENARIO", "MIXED"];
const difficultyOptions = ["Easy", "Medium", "Hard"];

const AddInterviewQuestionBank = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const { gen_id, edit } = location.state || "";

  const [loading, setLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationMessage, setGenerationMessage] = useState("");
  const [generationTitle, setGenerationTitle] = useState("");
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [generationComplete, setGenerationComplete] = useState(false);
  const [genId, setGenId] = useState(null);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editingQuestionData, setEditingQuestionData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [regeneratingQuestionId, setRegeneratingQuestionId] = useState(null);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [regenerateQuestionData, setRegenerateQuestionData] = useState(null);
  const [userFeedback, setUserFeedback] = useState("");
  const [jobDescriptionDialogOpen, setJobDescriptionDialogOpen] = useState(false);
  const [jobDescription, setJobDescription] = useState("");
  const [loadingJobDescription, setLoadingJobDescription] = useState(false);

  const streamQuestionsRef = useRef([]);
  const streamKeysRef = useRef([]);
  const streamDoneRef = useRef(false);
  const streamAbortRef = useRef(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    // Validate while typing so the limit messages show (and clear) inline, before Generate
    mode: "onChange",
    defaultValues: {
      program_id: null,
      topic: "",
      experience_level: undefined,
      question_type: "",
      difficulty: "",
      no_of_questions: undefined,
    },
  });

  const [programOptions, setProgramOptions] = useState([]);

  // Common text field style matching AddCategory.js
  const commonTextFieldStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "white" },
      "&:hover fieldset": { borderColor: "#d1d1d1" },
      "&.Mui-focused fieldset": {
        borderColor: "#1c97e3",
      },
    },
  };

  const noSpinnerNumberStyle = {
    ...commonTextFieldStyle,
    "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
    {
      WebkitAppearance: "none",
      margin: 0,
    },
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
  };

  // Fetch programs dropdown based on institute_id from login details
  const getProgramsDropdown = async (instituteId) => {
    if (!instituteId) {
      setProgramOptions([]);
      return;
    }
    try {
      const { data } = await api.post("GetProgramsDropdown", {
        institute_id: +instituteId,
      });
      setProgramOptions(data?.data ?? []);
    } catch (error) {
      console.error("Error fetching programs:", error);
      setProgramOptions([]);
    }
  };

  // Fetch programs dropdown on component mount
  useEffect(() => {
    const instituteId = localStorage.getItem("InstituteId");
    if (instituteId) {
      getProgramsDropdown(instituteId);
    }
  }, []);

  // Fetch program outcome/job description
  const fetchJobDescription = async (programId) => {
    if (!programId) {
      showSnackbar("error", "Please select a job role first");
      return;
    }

    setLoadingJobDescription(true);
    try {
      const { data } = await api.post("GetProgramOutcome", {
        program_id: programId,
      });

      if (data?.status === "success" && data?.data?.program_outcome) {
        setJobDescription(data.data.program_outcome);
        setJobDescriptionDialogOpen(true);
      } else {
        showSnackbar("error", data?.message || "Failed to fetch job description");
      }
    } catch (error) {
      console.error("Error fetching job description:", error);
      showSnackbar("error", error?.response?.data?.message || error?.message || "An error occurred while fetching job description");
    } finally {
      setLoadingJobDescription(false);
    }
  };

  // Build URL for streaming endpoint
  const buildGenerateUrl = () => {
    const base = (api.defaults.baseURL || "").replace(/\/+$/, "");
    return `${base}/GenerateInterviewQuestionBank`;
  };

  // Handle stream chunks
  const handleStreamChunk = (chunk) => {
    console.log("Stream chunk received:", chunk);

    if (chunk.status === "complete" || chunk.type === "done") {
      streamDoneRef.current = true;
      setGenerationComplete(true);
      // Capture gen_id from completion chunk
      if (chunk.gen_id) {
        setGenId(chunk.gen_id);
      }
      return;
    }

    if (chunk.type === "batch_result" && chunk.questions && Array.isArray(chunk.questions)) {
      console.log("Adding questions:", chunk.questions);
      streamQuestionsRef.current = [...streamQuestionsRef.current, ...chunk.questions];
      setGeneratedQuestions(streamQuestionsRef.current);
      // Capture gen_id from batch result chunks
      if (chunk.gen_id) {
        setGenId(chunk.gen_id);
      }
    }

    if (chunk.session_key) {
      streamKeysRef.current = [...new Set([...streamKeysRef.current, chunk.session_key])];
    }

    if (chunk.message) {
      setGenerationMessage(chunk.message);
    }
  };

  // Consume the SSE stream
  const consumeQuestionStream = async (payload) => {
    const token = localStorage.getItem("token");
    const abortController = new AbortController();
    streamAbortRef.current = abortController;

    const response = await fetch(buildGenerateUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      signal: abortController.signal,
    });

    if (response.status === 401) {
      localStorage.clear();
      window.location.href = "/";
      return;
    }

    if (!response.ok) {
      throw new Error(`Generation failed (HTTP ${response.status})`);
    }

    const contentType = response.headers.get("content-type") || "";

    // Safety net for non-SSE responses
    if (!contentType.includes("text/event-stream") || !response.body) {
      const dataJson = await response.json();
      const questions = dataJson?.questions || [];
      streamQuestionsRef.current = questions;
      setGeneratedQuestions(questions);
      streamDoneRef.current = true;
      setGenerationComplete(true);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop();
      for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line.startsWith("data:")) continue;
        const jsonStr = line.slice(5).trim();
        if (!jsonStr) continue;
        try {
          handleStreamChunk(JSON.parse(jsonStr));
        } catch (parseErr) {
          console.warn("Skipping malformed SSE chunk:", jsonStr);
        }
      }
    }
  };

  const onSubmit = async (data) => {
    setIsGenerating(true);
    setGenerationTitle("AI is crafting your interview questions");
    setGenerationMessage("Generating interview questions...");
    setGeneratedQuestions([]);
    streamQuestionsRef.current = [];
    streamKeysRef.current = [];
    streamDoneRef.current = false;
    setGenerationComplete(false);
    setIsStreaming(true);

    const payload = {
      program_id: data.program_id?.program_id,
      topic: data.topic || null,
      experience_level: String(data.experience_level),
      question_type: data.question_type,
      difficulty: data.difficulty,
      no_of_questions: data.no_of_questions,
    };

    console.log("Submitting payload:", payload);

    try {
      await consumeQuestionStream(payload);
      if (streamDoneRef.current) {
        showSnackbar("success", "Interview question bank generated successfully");
      }
    } catch (error) {
      if (error?.name === "AbortError") return;
      console.log("Streaming error:", error);
      showSnackbar("error", error?.message || "An error occurred while generating questions");
    } finally {
      setIsGenerating(false);
      setIsStreaming(false);
      setGenerationComplete(true);
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    try {
      const payload = {
        gen_id: genId
      };

      console.log("Saving interview question bank with payload:", payload);

      const res = await api.post("SaveInterviewQuestionBank", payload);

      if (res?.data?.status === "success") {
        showSnackbar("success", res?.data?.message || "Interview question bank saved successfully!");
        localStorage.removeItem("generatedQuestions");
        localStorage.removeItem("sessionKeys");
        navigate("/interview-question-bank");
      } else {
        showSnackbar("error", res?.data?.message || "Failed to save interview question bank");
      }
    } catch (error) {
      showSnackbar("error", error?.response?.data?.message || error?.message || "An error occurred while saving");
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = () => {
    reset();
    localStorage.removeItem("generatedQuestions");
    localStorage.removeItem("sessionKeys");
    setGeneratedQuestions([]);
    streamQuestionsRef.current = [];
    streamKeysRef.current = [];
    setGenerationComplete(false);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestionId(question.question_program_mapping_id);
    setEditingQuestionData({
      gen_id: genId,
      question_program_mapping_id: question.question_program_mapping_id,
      question: question.question,
      answer: question.answer,
      question_type: question.question_type,
    });
  };

  const handleCancelEdit = () => {
    setEditingQuestionId(null);
    setEditingQuestionData(null);
  };

  const handleSaveQuestion = async () => {
    if (!editingQuestionData) return;

    setSaving(true);
    try {
      const payload = {
        gen_id: editingQuestionData.gen_id,
        question_program_mapping_id: editingQuestionData.question_program_mapping_id,
        question: editingQuestionData.question,
        answer: editingQuestionData.answer,
        question_type: editingQuestionData.question_type,
      };

      const res = await api.post("EditInterviewQuestion", payload);

      if (res?.data?.status === "success") {
        showSnackbar("success", "Question updated successfully");
        setGeneratedQuestions((prevQuestions) =>
          prevQuestions.map((q) =>
            q.question_program_mapping_id === editingQuestionData.question_program_mapping_id
              ? {
                  ...q,
                  question: editingQuestionData.question,
                  answer: editingQuestionData.answer,
                  question_type: editingQuestionData.question_type,
                }
              : q
          )
        );
        handleCancelEdit();
      } else {
        showSnackbar("error", res?.data?.message || "Failed to update question");
      }
    } catch (error) {
      showSnackbar("error", error?.response?.data?.message || error?.message || "An error occurred while updating question");
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateAnswer = (question) => {
    setRegenerateQuestionData(question);
    setUserFeedback("");
    setRegenerateDialogOpen(true);
  };

  const handleCloseRegenerateDialog = () => {
    setRegenerateDialogOpen(false);
    setRegenerateQuestionData(null);
    setUserFeedback("");
  };

  const handleConfirmRegenerate = async () => {
    if (!regenerateQuestionData || !userFeedback.trim()) {
      showSnackbar("error", "Please provide feedback for regeneration");
      return;
    }

    setRegeneratingQuestionId(regenerateQuestionData.question_program_mapping_id);
    setRegenerateDialogOpen(false);

    try {
      const payload = {
        gen_id: genId,
        question_program_mapping_id: regenerateQuestionData.question_program_mapping_id,
        prompt: userFeedback,
      };

      const res = await api.post("RegenerateInterviewAnswers", payload);

      if (res?.data?.status === "success") {
        showSnackbar("success", "Answer regenerated successfully");
        // Update the question in the local state with the new answer
        setGeneratedQuestions((prevQuestions) =>
          prevQuestions.map((q) =>
            q.question_program_mapping_id === regenerateQuestionData.question_program_mapping_id
              ? {
                  ...q,
                  answer: res.data.data.updated_answer.answer,
                }
              : q
          )
        );
      } else {
        showSnackbar("error", res?.data?.message || "Failed to regenerate answer");
      }
    } catch (error) {
      showSnackbar("error", error?.response?.data?.message || error?.message || "An error occurred while regenerating answer");
    } finally {
      setRegeneratingQuestionId(null);
      setRegenerateQuestionData(null);
      setUserFeedback("");
    }
  };

  useEffect(() => {
    return () => {
      if (streamAbortRef.current) {
        streamAbortRef.current.abort();
      }
    };
  }, []);

  const formDisabled = loading || isGenerating || isStreaming;

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {loading && <OverAllLoader message="Please wait..." />}

      <Box>
        {/* Back Button */}
        <Grid
          item
          sx={{
            display: "flex",
            alignItems: "center",
            color: "#1c97e3",
            gap: "4px",
            cursor: "pointer",
            width: "fit-content",
            mb: 2,
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              window.history.back();
            }
          }}
          onClick={() => navigate("/interview-question-bank")}
        >
          <ArrowBackIcon sx={{ fontSize: "17px" }} />
          <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>Back</Typography>
        </Grid>

        {/* Main Card */}
        <Box>
          {/* Header */}
          <Grid
            sx={{
              pb: 2,
              display: "flex",
              gap: 1,
              alignItems: "center",
              borderBottom: "1px solid #d1d1d1",
            }}
          >
            <Grid
              sx={{
                backgroundColor: "#1c97e3",
                p: 0.5,
                borderRadius: "10px",
              }}
            >
              <CategoryIcon sx={{ color: "white", fontSize: "35px" }} />
            </Grid>
            <Grid>
              <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>
                {edit === true ? "Update" : "Add"} Interview Question Bank
              </Typography>
            </Grid>
          </Grid>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                rowGap: 3,
                p: 3,
              }}
            >
              {/* Job Role Section */}
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <CategoryIcon sx={{ color: "#1c97e3", fontSize: "20px" }} />
                <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1c97e3" }}>
                  Job Role Details
                </Typography>
              </Box>

              <Box
                sx={{
                  border: "1px solid #e3eaf2",
                  borderRadius: "14px",
                  p: 3,
                  backgroundColor: "#fbfdff",
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 3,
                }}
              >
                <Grid sx={{ display: "flex", gap: 5 }}>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Job Role <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="program_id"
                      control={control}
                      render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                        <Autocomplete
                          {...field}
                          options={programOptions}
                          disabled={formDisabled}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return option.program_name || "";
                          }}
                          isOptionEqualToValue={(option, value) =>
                            option?.program_id === value?.program_id
                          }
                          renderOption={(props, option) => (
                            <li {...props} key={option.program_id}>
                              {option.program_code ? `[${option.program_code}] ` : ""}
                              {option.program_name}
                            </li>
                          )}
                          value={value}
                          onChange={(event, newValue) => {
                            onChange(newValue);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Job Role"
                              error={!!error}
                              helperText={error?.message}
                              sx={commonTextFieldStyle}
                            />
                          )}
                        />
                      )}
                    />
                    {watch("program_id")?.program_id && (
                      <Button
                        onClick={() => fetchJobDescription(watch("program_id").program_id)}
                        disabled={loadingJobDescription}
                        startIcon={loadingJobDescription ? <CircularProgress size={16} /> : <DescriptionIcon />}
                        sx={{
                          mt: 1,
                          textTransform: "none",
                          fontSize: "13px",
                          fontWeight: 600,
                          borderRadius: "8px",
                          px: 2,
                          py: 0.75,
                          backgroundColor: "#e3f2fd",
                          color: "#03438A",
                          "&:hover": {
                            backgroundColor: "#bbdefb",
                            color: "#03438A",
                          },
                          "&:disabled": {
                            backgroundColor: "#f5f5f5",
                            color: "#9e9e9e",
                          },
                        }}
                      >
                        {loadingJobDescription ? "Loading..." : "View Job Description"}
                      </Button>
                    )}
                  </Grid>

                  <Grid sx={{ width: "100%" }}>
                    <Typography>Topic</Typography>
                    <Controller
                      name="topic"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder="Enter topic"
                          fullWidth
                          disabled={formDisabled}
                          error={!!errors.topic}
                          helperText={errors.topic?.message}
                          sx={commonTextFieldStyle}
                        />
                      )}
                    />
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#757575",
                        mt: 0.5,
                        fontStyle: "italic",
                      }}
                    >
                      Questions will be generated based on the Job Description and your topic input
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Question Configuration Section */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                <CategoryIcon sx={{ color: "#1c97e3", fontSize: "20px" }} />
                <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1c97e3" }}>
                  Question Configuration
                </Typography>
              </Box>

              <Box
                sx={{
                  border: "1px solid #e3eaf2",
                  borderRadius: "14px",
                  p: 3,
                  backgroundColor: "#fbfdff",
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 3,
                }}
              >
                <Grid sx={{ display: "flex", gap: 5 }}>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Experience Years <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="experience_level"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          type="number"
                          placeholder="Enter years of experience"
                          fullWidth
                          disabled={formDisabled}
                          error={!!error}
                          helperText={error?.message}
                          inputProps={{ min: 0, max: 99, step: 1 }}
                          sx={noSpinnerNumberStyle}
                          onChange={(e) => {
                            const value = e.target.value === "" ? undefined : Number(e.target.value);
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Question Type <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="question_type"
                      control={control}
                      render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                        <Autocomplete
                          {...field}
                          options={questionTypeOptions}
                          disabled={formDisabled}
                          value={value}
                          onChange={(event, newValue) => {
                            onChange(newValue);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Question Type"
                              error={!!error}
                              helperText={error?.message}
                              sx={commonTextFieldStyle}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Grid sx={{ display: "flex", gap: 5 }}>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Difficulty <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="difficulty"
                      control={control}
                      render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                        <Autocomplete
                          {...field}
                          options={difficultyOptions}
                          disabled={formDisabled}
                          value={value}
                          onChange={(event, newValue) => {
                            onChange(newValue);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Difficulty"
                              error={!!error}
                              helperText={error?.message}
                              sx={commonTextFieldStyle}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>

                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Number of Questions <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="no_of_questions"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          placeholder="10"
                          fullWidth
                          disabled={formDisabled}
                          inputProps={{ min: 1, max: 25 }}
                          error={!!errors.no_of_questions}
                          helperText={errors.no_of_questions?.message}
                          sx={noSpinnerNumberStyle}
                          onChange={(e) => {
                            const value = e.target.value === "" ? undefined : Number(e.target.value);
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 3,
                }}
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleClearAll}
                  disabled={formDisabled}
                  sx={{ textTransform: "none", borderRadius: "12px", px: 3, fontWeight: 600 }}
                >
                  Clear
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formDisabled}
                  sx={{
                    textTransform: "none",
                    borderRadius: "12px",
                    px: 4,
                    fontWeight: 600,
                    display: "flex",
                    gap: 1,
                  }}
                >
                  <AutoAwesomeIcon fontSize="small" />
                  Generate Questions
                </Button>
              </Box>
            </Box>
          </form>

          {/* Generated Questions Section */}
          {(generatedQuestions.length > 0 || isGenerating) && (
            <Box
              sx={{
                mt: 4,
                border: "1px solid #d1d1d1",
                borderRadius: "12px",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                backgroundColor: "white",
                p: 4,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #E0E5F2", pb: 2, mb: 3 }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <CategoryIcon sx={{ color: "#03438A" }} />
                  <Typography sx={{ fontSize: "20px", fontWeight: "bold", color: "#03438A" }}>
                    Generated Questions
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {isStreaming && <CircularProgress size={16} sx={{ color: "#1c97e3" }} />}
                  <Typography sx={{ fontSize: "13px", fontWeight: 600, color: isStreaming ? "#1c97e3" : "#2e7d32" }}>
                    {generatedQuestions.length} question{generatedQuestions.length === 1 ? "" : "s"}
                    {isStreaming ? " generating…" : " generated"}
                  </Typography>
                </Box>
              </Box>

              {!generationComplete ? (
                // Skeleton loader while streaming
                <>
                  {[1, 2, 3].map((skeletonIndex) => (
                    <Box
                      key={skeletonIndex}
                      sx={{
                        mb: 3,
                        p: { xs: 2.5, md: 3 },
                        borderRadius: "16px",
                        border: "1px solid #eef2f7",
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 1.5, mb: 2, alignItems: "flex-start" }}>
                        <Skeleton variant="rounded" width={30} height={30} sx={{ borderRadius: "9px" }} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton variant="text" width="90%" height={24} />
                          <Skeleton variant="text" width="75%" height={20} sx={{ mt: 1 }} />
                        </Box>
                      </Box>
                      <Skeleton variant="rounded" width="100%" height={60} sx={{ borderRadius: "12px" }} />
                    </Box>
                  ))}
                </>
              ) : (
                // Actual questions after streaming is complete
                generatedQuestions.map((question, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 3,
                      p: { xs: 2.5, md: 3 },
                      borderRadius: "16px",
                      border: "1px solid #eef2f7",
                      backgroundColor: "#ffffff",
                      boxShadow: "0 1px 3px rgba(16,24,40,0.04)",
                      transition: "box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease",
                      "&:hover": {
                        boxShadow: "0 10px 28px rgba(28,151,227,0.10)",
                        borderColor: "#d8e9fb",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1.5, mb: 2, alignItems: "flex-start" }}>
                      <Box
                        sx={{
                          flexShrink: 0,
                          minWidth: "30px",
                          height: "30px",
                          px: 0.5,
                          borderRadius: "9px",
                          background: "linear-gradient(135deg, #1c97e3 0%, #03438A 100%)",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "13px",
                          fontWeight: 700,
                          boxShadow: "0 2px 6px rgba(3,67,138,0.25)",
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Box sx={{ flex: 1, pt: "2px" }}>
                        {regeneratingQuestionId === question.question_program_mapping_id ? (
                          // Skeleton loader for question during regeneration
                          <Box>
                            <Skeleton variant="text" width="90%" height={24} />
                            <Skeleton variant="text" width="75%" height={20} sx={{ mt: 1 }} />
                          </Box>
                        ) : (
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            {editingQuestionId === question.question_program_mapping_id ? (
                              <TextField
                                fullWidth
                                multiline
                                rows={2}
                                value={editingQuestionData?.question || ""}
                                onChange={(e) =>
                                  setEditingQuestionData({ ...editingQuestionData, question: e.target.value })
                                }
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": { borderColor: "#1c97e3" },
                                    "&:hover fieldset": { borderColor: "#1c97e3" },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#1c97e3",
                                    },
                                  },
                                }}
                              />
                            ) : (
                              <>
                                <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#333", lineHeight: 1.7, flex: 1 }}>
                                  {question.question || question.text || "Question text"}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 0.5, ml: 1 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditQuestion(question)}
                                    sx={{ color: "#1c97e3" }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRegenerateAnswer(question)}
                                    disabled={regeneratingQuestionId === question.question_program_mapping_id}
                                    sx={{ color: "#ff9800" }}
                                  >
                                    {regeneratingQuestionId === question.question_program_mapping_id ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <AutorenewIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Box>
                              </>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {question.answer && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2.5,
                          borderRadius: "12px",
                          backgroundColor: editingQuestionId === question.question_program_mapping_id ? "#f0f9ff" : (regeneratingQuestionId === question.question_program_mapping_id ? "#f5f5f5" : "#e8f5e9"),
                          border: editingQuestionId === question.question_program_mapping_id ? "1px solid #1c97e3" : (regeneratingQuestionId === question.question_program_mapping_id ? "1px solid #e0e0e0" : "1px solid #a5d6a7"),
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: "12px", color: editingQuestionId === question.question_program_mapping_id ? "#1c97e3" : (regeneratingQuestionId === question.question_program_mapping_id ? "#757575" : "#2e7d32"), textTransform: "uppercase", letterSpacing: "0.5px" }}>
                            Answer
                          </Typography>
                          {regeneratingQuestionId === question.question_program_mapping_id && (
                            <CircularProgress size={16} sx={{ color: "#757575" }} />
                          )}
                        </Box>
                        {editingQuestionId === question.question_program_mapping_id ? (
                          <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={editingQuestionData?.answer || ""}
                            onChange={(e) =>
                              setEditingQuestionData({ ...editingQuestionData, answer: e.target.value })
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": { borderColor: "#1c97e3" },
                                "&:hover fieldset": { borderColor: "#1c97e3" },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#1c97e3",
                                },
                              },
                            }}
                          />
                        ) : regeneratingQuestionId === question.question_program_mapping_id ? (
                          // Skeleton loader for answer during regeneration
                          <>
                            <Skeleton variant="text" width="100%" height={20} />
                            <Skeleton variant="text" width="95%" height={20} sx={{ mt: 1 }} />
                            <Skeleton variant="text" width="90%" height={20} sx={{ mt: 1 }} />
                            <Skeleton variant="text" width="85%" height={20} sx={{ mt: 1 }} />
                          </>
                        ) : (
                          <Typography sx={{ fontSize: "14px", color: "#1b5e20", lineHeight: 1.7 }}>
                            {question.answer}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Edit Action Buttons */}
                    {editingQuestionId === question.question_program_mapping_id && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3, pt: 2, borderTop: "1px solid #e0e0e0" }}>
                        <Button
                          variant="outlined"
                          onClick={handleCancelEdit}
                          disabled={saving}
                          sx={{ borderRadius: "8px", textTransform: "none" }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleSaveQuestion}
                          disabled={saving}
                          sx={{ borderRadius: "8px", textTransform: "none" }}
                        >
                          {saving ? "Saving..." : "Save Changes"}
                        </Button>
                      </Box>
                    )}
                  </Box>
                ))
              )}

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2, borderTop: "1px solid #E0E5F2", pt: 3 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={isStreaming}
                  onClick={() => {
                    setGeneratedQuestions([]);
                    streamQuestionsRef.current = [];
                    streamKeysRef.current = [];
                    setGenerationComplete(false);
                    localStorage.removeItem("generatedQuestions");
                    localStorage.removeItem("sessionKeys");
                  }}
                  sx={{ textTransform: "none", borderRadius: "12px", px: 4, fontWeight: 600 }}
                >
                  Cancel Questions
                </Button>
                <Button
                  onClick={handleVerify}
                  variant="contained"
                  color="primary"
                  disabled={!generationComplete || generatedQuestions.length === 0}
                  sx={{ textTransform: "none", borderRadius: "12px", px: 4, fontWeight: 600 }}
                >
                  Submit Questions
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Regenerate Answer Dialog */}
      <Dialog
        open={regenerateDialogOpen}
        onClose={handleCloseRegenerateDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px" },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "20px" }}>
          Regenerate
        </DialogTitle>
        <DialogContent>
          <Box>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#333", mb: 1 }}>
              Your Feedback for Regeneration:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Describe how you want the answer to be improved (e.g., 'Make it more detailed', 'Add more examples', 'Focus on senior-level concepts')"
              value={userFeedback}
              onChange={(e) => setUserFeedback(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#d1d1d1" },
                  "&:hover fieldset": { borderColor: "#1c97e3" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1c97e3",
                  },
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleCloseRegenerateDialog}
            variant="outlined"
            sx={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRegenerate}
            variant="contained"
            sx={{ borderRadius: "8px" }}
          >
            Regenerate Answer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Job Description Dialog */}
      <Dialog
        open={jobDescriptionDialogOpen}
        onClose={() => setJobDescriptionDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px" },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "20px" }}>
          Job Description
        </DialogTitle>
        <DialogContent>
          <Box>
            {loadingJobDescription ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box
                sx={{
                  fontSize: "14px",
                  color: "#333",
                  lineHeight: 1.7,
                  "& ul": {
                    paddingLeft: "20px",
                    margin: "8px 0",
                  },
                  "& ol": {
                    paddingLeft: "20px",
                    margin: "8px 0",
                  },
                  "& li": {
                    marginBottom: "4px",
                  },
                  "& strong": {
                    fontWeight: 700,
                    color: "#03438A",
                  },
                  "& h1, & h2, & h3": {
                    fontWeight: 700,
                    color: "#03438A",
                    mt: 2,
                    mb: 1,
                  },
                  "& p": {
                    mb: 1,
                  },
                  "& code": {
                    backgroundColor: "#f5f5f5",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    fontFamily: "monospace",
                    fontSize: "13px",
                  },
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {jobDescription}
                </ReactMarkdown>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setJobDescriptionDialogOpen(false)}
            variant="contained"
            sx={{ borderRadius: "8px" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddInterviewQuestionBank;
